import { Alert, Stack, type Theme } from "@mui/material";

import { Text } from "../../components/Text";
import { getMutedText, getStrongText } from "../themeHelpers";
import { type SupportArticleGroup } from "../types";
import { HelpCenterLoadingState } from "./HelpCenterLoadingState";
import { SupportArticleCard } from "./SupportArticleCard";

function getSectionHeadingSx(theme: Theme, isMobile: boolean) {
  return {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: isMobile ? theme.typography.h6.fontSize : theme.typography.h5.fontSize,
    lineHeight: 1.2,
    color: getStrongText(theme),
  };
}

function getGroupHeadingSx(theme: Theme) {
  return {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: "0.6875rem",
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    color: getMutedText(theme),
  };
}

interface HelpCenterResultsSurfaceProps {
  articleGroups: SupportArticleGroup[];
  hasNoSearchResults: boolean;
  hasNoSupportArticles: boolean;
  isError: boolean;
  isLoading: boolean;
  isMobile: boolean;
  resultsTitle: string;
  shouldShowGroupTitles: boolean;
}

export function HelpCenterResultsSurface(props: HelpCenterResultsSurfaceProps) {
  const {
    articleGroups,
    hasNoSearchResults,
    hasNoSupportArticles,
    isError,
    isLoading,
    isMobile,
    resultsTitle,
    shouldShowGroupTitles,
  } = props;

  return (
    <Stack
      spacing={3}
      sx={(theme) => ({
        padding: theme.spacing(isMobile ? 3 : 4),
        borderRadius: theme.borderRadius?.xLarge,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Text bold variant="body1" sx={(theme) => getSectionHeadingSx(theme, isMobile)}>
        {resultsTitle}
      </Text>

      {isLoading && <HelpCenterLoadingState />}
      {isError && <Alert severity="error">Error loading support articles.</Alert>}
      {hasNoSupportArticles && <Text>No support articles are available right now.</Text>}
      {hasNoSearchResults && (
        <Text sx={(theme) => ({ color: getMutedText(theme) })}>
          No support articles match your current search.
        </Text>
      )}
      {!isLoading && !isError && articleGroups.length > 0 && (
        <Stack spacing={3}>
          {articleGroups.map((articleGroup) => (
            <Stack key={articleGroup.category} spacing={2}>
              {shouldShowGroupTitles ? (
                <Text variant="body2" sx={(theme) => getGroupHeadingSx(theme)}>
                  {articleGroup.category}
                </Text>
              ) : null}
              <Stack spacing={2}>
                {articleGroup.supportArticles.map((supportArticle) => (
                  <SupportArticleCard key={supportArticle.id} supportArticle={supportArticle} />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
